/**
 * This module handles categorical/discrete data visualization where values are mapped to distinct colours.
 * It's used when you have a finite set of categories (e.g., hardness: "Soft", "Medium", "Hard")
 * and want each category to have its own colour.
 */

import * as d3 from 'd3';

import type { ColourLegendItem, ColourScale, ColourValue } from './types';

/** Default colour palette for discrete scales (D3 Tableau10) */
const DEFAULT_PALETTE = d3.schemeTableau10;

/** Golden-angle step used to generate deterministic overflow hues */
const OVERFLOW_HUE_STEP = 137.508;

/** Saturation used for generated overflow colours */
const OVERFLOW_SATURATION = 0.68;

/** Lightness variants used for generated overflow colours */
const OVERFLOW_LIGHTNESS = [0.42, 0.5, 0.58] as const;

/** Maximum number of unique hex colours available */
const MAX_HEX_COLOUR_COUNT = 0x1000000;

/** Maximum overflow attempts before falling back to direct hex generation */
const MAX_OVERFLOW_ATTEMPT_MULTIPLIER = 8;

/** Default opacity for discrete colours */
const DEFAULT_OPACITY = 0.8;

/** Default colour when value not found */
const DEFAULT_COLOUR: ColourValue = {
  fill: '#888888',
  stroke: '#666666',
  opacity: DEFAULT_OPACITY,
};

/** Options for creating a discrete colour scale */
type DiscreteScaleOptions = {
  /** Colour palette to cycle through (defaults to D3 Tableau10) */
  palette?: readonly string[];
  /** Opacity for all colours (defaults to 0.8) */
  opacity?: number;
  /** Custom colour mapping for specific values (takes precedence over palette) */
  customMapping?: Record<string, ColourValue>;
  /** Custom sort function for legend order */
  sortValues?: (a: string, b: string) => number;
};

/**
 * Creates a ColourValue from a fill colour string.
 * Generates a darker stroke colour automatically.
 *
 * @param fill - CSS colour string for fill
 * @param opacity - Opacity value (0-1)
 * @returns ColourValue with fill, stroke, and opacity
 */
function createColourValue(fill: string, opacity: number = DEFAULT_OPACITY): ColourValue {
  const stroke = d3.color(fill)?.darker(1).formatHex() ?? '#000000';
  return { fill, stroke, opacity };
}

/**
 * Normalizes a colour string for stable uniqueness checks.
 */
function normalizeColour(fill: string): string {
  return d3.color(fill)?.formatHex().toLowerCase() ?? fill.trim().toLowerCase();
}

/**
 * Generates a deterministic overflow colour in hex format.
 */
function createOverflowColour(index: number): string {
  const hue = (index * OVERFLOW_HUE_STEP) % 360;
  const lightness = OVERFLOW_LIGHTNESS[index % OVERFLOW_LIGHTNESS.length];
  return d3.hsl(hue, OVERFLOW_SATURATION, lightness).formatHex();
}

/**
 * Generates a deterministic fallback colour from the 24-bit hex colour space.
 */
function createFallbackOverflowColour(index: number): string {
  return `#${index.toString(16).padStart(6, '0')}`;
}

/**
 * Builds a unique palette large enough for the required number of values.
 * Starts with provided palette colours and extends it with generated colours when needed.
 */
function buildUniquePalette(requiredCount: number, palette: readonly string[], reservedColours: Set<string>): string[] {
  if (requiredCount + reservedColours.size > MAX_HEX_COLOUR_COUNT) {
    throw new Error('Unable to build a unique palette for more than 16777216 reserved and generated colours.');
  }

  const colours: string[] = [];
  const usedColours = new Set(reservedColours);

  for (const fill of palette) {
    const normalizedFill = normalizeColour(fill);
    if (usedColours.has(normalizedFill)) continue;

    colours.push(fill);
    usedColours.add(normalizedFill);

    if (colours.length >= requiredCount) {
      return colours;
    }
  }

  let overflowIndex = 0;
  let overflowAttempts = 0;
  const maxOverflowAttempts = Math.max(requiredCount * MAX_OVERFLOW_ATTEMPT_MULTIPLIER, palette.length);

  while (colours.length < requiredCount && overflowAttempts < maxOverflowAttempts) {
    const fill = createOverflowColour(overflowIndex);
    overflowIndex += 1;
    overflowAttempts += 1;

    const normalizedFill = normalizeColour(fill);
    if (usedColours.has(normalizedFill)) continue;

    colours.push(fill);
    usedColours.add(normalizedFill);
  }

  let fallbackIndex = 0;
  while (colours.length < requiredCount && fallbackIndex < MAX_HEX_COLOUR_COUNT) {
    const fill = createFallbackOverflowColour(fallbackIndex);
    fallbackIndex += 1;

    const normalizedFill = normalizeColour(fill);
    if (usedColours.has(normalizedFill)) continue;

    colours.push(fill);
    usedColours.add(normalizedFill);
  }

  if (colours.length < requiredCount) {
    throw new Error('Unable to build a unique palette with the available colour space.');
  }

  return colours;
}

/**
 * Creates a discrete colour scale for categorical properties.
 * Automatically extracts unique values and assigns colours from a palette.
 *
 * @template T - The type of data items
 * @param data - Array of data items
 * @param property - Categorical property to base the scale on
 * @param options - Scale configuration options
 * @returns A discrete colour scale configuration
 *
 * @example
 * ```ts
 * const scale = createDiscreteScale(blocks, 'geology', {
 *   palette: d3.schemeCategory10,
 *   opacity: 0.7,
 * });
 *
 * // Get colour for a value
 * const colour = scale.getColour('SANDSTONE');
 *
 * // Access legend for UI
 * scale.legend.forEach(item => console.log(item.label, item.colour));
 * ```
 */
function createDiscreteScale<T extends Record<string, unknown>>(
  data: T[],
  property: keyof T & string,
  options: DiscreteScaleOptions = {},
): Extract<ColourScale, { type: 'discrete' }> {
  const {
    palette = DEFAULT_PALETTE,
    opacity = DEFAULT_OPACITY,
    customMapping = {},
    sortValues = (a, b) => a.localeCompare(b),
  } = options;

  // Extract unique values and sort
  const uniqueValues = [...new Set(data.map((item) => String(item[property])))].sort(sortValues);
  const valuesWithoutCustomMapping = uniqueValues.filter((value) => !customMapping[value]);

  const reservedColours = new Set(
    uniqueValues.filter((value) => customMapping[value]).map((value) => normalizeColour(customMapping[value].fill)),
  );
  const uniquePalette = buildUniquePalette(valuesWithoutCustomMapping.length, palette, reservedColours);

  // Build colour map: custom mapping takes precedence, otherwise assign unique colours
  const colourMap = new Map<string, ColourValue>();
  let paletteIndex = 0;
  uniqueValues.forEach((value) => {
    if (customMapping[value]) {
      colourMap.set(value, customMapping[value]);
    } else {
      const fill = uniquePalette[paletteIndex];
      paletteIndex += 1;
      colourMap.set(value, createColourValue(fill, opacity));
    }
  });

  // Generate legend items
  const legend: ColourLegendItem<string, ColourValue>[] = uniqueValues.map((value) => ({
    value,
    colour: colourMap.get(value) ?? DEFAULT_COLOUR,
    label: value,
  }));

  return {
    type: 'discrete',
    property,
    legend,
    getColour: (value: string) => colourMap.get(value) ?? DEFAULT_COLOUR,
  };
}

export {
  createColourValue,
  createDiscreteScale,
  DEFAULT_COLOUR,
  DEFAULT_OPACITY,
  DEFAULT_PALETTE,
  type DiscreteScaleOptions,
};
